import toast from 'react-hot-toast';
import { capitalCase } from 'change-case';
import uniqBy from 'lodash/uniqBy';
import omit from 'lodash/omit';
import type { useMutation, useQuery } from '@tanstack/react-query';
import { create } from 'zustand';
import { z } from 'zod';

import { APIEndPoints, Request } from '../apis';
import type { TMerchant } from '../components/types';
import type { TAccountProduct, TProduct, TProductFeeData } from '../common';
import { Logger, ProductSchema } from '../common';
import { MINUTE } from '../common/utils/constants';
import type { TMerchantSchema } from './MerchantSchema';
import { MerchantSchema } from './MerchantSchema';
import type { MerchantId } from './MerchantIdSchema';

type CurrentProductIds = ReadonlyArray<string>;
type CurrentProducts = ReadonlyArray<TAccountProduct>;
type PartialProductData = Partial<Omit<TAccountProduct, 'fees'>>;
type ProductId = TAccountProduct['id'];
type ProductFees = TAccountProduct['fees'];

export type AccountStoreState = {
  merchantDetails: TMerchant | null;
  currentProductIds: CurrentProductIds;
  currentProducts: CurrentProducts;
  currentProductsLoading: boolean;
  // Mutations
  setMerchantDetails: (merchantDetails: TMerchant) => void;
  setCurrentProductIds: (availableProducts: CurrentProductIds) => void;
  setCurrentProductsLoading: (isLoading: boolean) => void;
  addCurrentProduct: (currentProduct: TAccountProduct) => void;
  updateProductFees: (fees: ProductFees, id: ProductId) => void;
  updateProductData: (data: PartialProductData, id: ProductId) => void;
  clearState: () => void;
};

const STARTING_STATE = {
  merchantDetails: null,
  currentProductIds: [],
  currentProducts: [],
  currentProductsLoading: false
} as const;

// export type SnapshotFee = {
//   id: string
//   name: string
//   state: string
//   amount: number
//   gst?: number
//   type: string
//   daysInArrears?: number
//   productFeeKey?: string
//   amortized?: boolean
//   feePaidBy?: string
//   externalPaymentSourceId?: string
//   systemCreationMethod?: string
//   conditions?: ProductFeeConditions
// }

export const productFeeDefaults = ({
  id = '',
  ...fee
}: Partial<TProductFeeData>): TProductFeeData => ({
  id,
  name: capitalCase(id),
  state: 'ACTIVE',
  amount: 0,
  type: 'FLAT',
  systemCreationMethod: 'Manual',
  required: false,
  ...fee
});

export const useAccountStore = create<AccountStoreState>((set) => ({
  ...STARTING_STATE,
  setMerchantDetails: (merchantDetails) => {
    set({
      merchantDetails,
      currentProductIds: merchantDetails.availableProducts
    });
  },
  setCurrentProductIds: (availableProducts) => {
    set({
      currentProductIds: availableProducts || []
    });
  },
  setCurrentProductsLoading: (isLoading) => {
    set({
      currentProductsLoading: isLoading
    });
  },
  addCurrentProduct: (currentProduct) => {
    set((state) => ({
      currentProducts: uniqBy([...state.currentProducts, currentProduct], 'id')
    }));
  },
  updateProductFees: (fees, productId) => {
    set((state) => {
      const product = state.currentProducts.find((p) => p.id === productId);

      if (!product) {
        console.error(`Product with id ${productId} not found`);

        throw new Error(`Product with id ${productId} not found`);
      }

      return {
        currentProducts: state.currentProducts.map((p) => {
          if (p.id === productId) {
            return {
              ...p,
              fees
            };
          }

          return p;
        })
      };
    });
  },
  updateProductData: (productData, productId) => {
    set((state) => ({
      currentProducts: state.currentProducts.map((p) => {
        if (p.id === productId) {
          // TODO log
          // console.log(`Update Product Data ${productId}:`, {
          //   ...p,
          //   ...productData
          // });
          return {
            ...p,
            ...productData
          };
        }

        return p;
      })
    }));
  },
  clearState: () => {
    set({ ...STARTING_STATE });
  }
}));

export const clearAccountStore = () => {
  useAccountStore.setState({ ...STARTING_STATE });
};

export const addProductToMerchant = ({
  merchantId,
  setCurrentProductIds,
  mutate
}: {
  merchantId: TMerchant['id'];
  setCurrentProductIds: AccountStoreState['setCurrentProductIds'];
  mutate: typeof useMutation;
}) =>
  mutate({
    mutationFn: (productCatalogueId: TAccountProduct['productCatalogueId']) => {
      return Request.post(
        `${APIEndPoints.merchants}/${merchantId}/availableProducts?access_token`,
        z.any(),
        { productType: productCatalogueId }
      );
    },
    onSuccess: async (data: TMerchant) => {
      setCurrentProductIds(data.availableProducts);
    },
    onError: (error) => {
      toast.error(
        "Error encountered adding product to merchant's available products"
      );
    }
  });

// @TODO - logically this should probably live in product context, but affects account state
export const mutateProductData = ({
  productId,
  merchantId,
  updateProductData,
  mutate
}: {
  productId: ProductId;
  merchantId: MerchantId;
  updateProductData: AccountStoreState['updateProductData'];
  mutate: typeof useMutation;
}) =>
  mutate({
    mutationFn: async (product: TProduct) => {
      return Request.patch(
        `${APIEndPoints.products}/merchant/${merchantId}/${productId}?access_token`,
        ProductSchema,
        product
      );
    },
    onSuccess: async (data: TProduct) => {
      // TODO log
      // console.log(`ON SUCCESS ${productId}: `, data);

      updateProductData(omit(data, 'fees'), data.id);
    },
    onError: (error) => {
      Logger.error(error as undefined);
      toast.error("Error encountered saving changes to product's data");
    }
  });

export const mutateProductFees = ({
  productId,
  merchantId,
  updateProductFees,
  mutate
}: {
  productId: ProductId;
  merchantId: MerchantId;
  updateProductFees: AccountStoreState['updateProductFees'];
  mutate: typeof useMutation;
}) =>
  mutate({
    mutationFn: (fees: ProductFees = {}) => {
      const feeData = Object.fromEntries(
        Object.entries(fees).map(([key, { id = key, ...fee }]) => [
          key,
          productFeeDefaults({ id, ...fee })
        ])
      );
      return Request.put(
        `${APIEndPoints.products}/merchant/${merchantId}/${productId}/fees?access_token`,
        z.any(),
        feeData
      );
    },
    onSuccess: async (data: TAccountProduct) => {
      // endpoint returns entire product with updated feeSnapshotId and indexed fees
      updateProductFees(data.fees, data.id);
    },
    onError: (error) => {
      Logger.error(error as undefined);
      toast.error("Error encountered saving changes to product's fees");
    }
  });

export const getMerchantDetails = ({
  merchantId,
  setMerchantDetails,
  query
}: {
  merchantId: string;
  setMerchantDetails: AccountStoreState['setMerchantDetails'];
  query: typeof useQuery;
}) => {
  return query(
    ['merchant', merchantId],
    () =>
      Request.get<TMerchantSchema>({
        path: `${APIEndPoints.merchants}/${merchantId}?access_token`,
        schema: MerchantSchema
      }),
    {
      onSuccess: (data) => {
        setMerchantDetails(data);
      },
      onError: (error) => {
        toast.error(
          'Something went wrong while trying to load this page. Please refresh and try again.'
        );
      },
      staleTime: 30 * MINUTE,
      refetchOnMount: 'always' as const
    }
  );
};

export const mutateMerchantDetails = ({
  merchantId,
  setMerchantDetails,
  mutate
}: {
  merchantId: TMerchantSchema['id'];
  setMerchantDetails: AccountStoreState['setMerchantDetails'];
  mutate: typeof useMutation;
}) =>
  mutate({
    mutationFn: (data: Partial<TMerchantSchema>) => {
      return Request.patch(
        `${APIEndPoints.merchants}/${merchantId}?access_token`,
        MerchantSchema,
        data
      );
    },
    onSuccess: async (data: TMerchantSchema) => {
      // endpoint returns entire product with updated feeSnapshotId and indexed fees
      setMerchantDetails(data);
    },
    onError: (error) => {
      Logger.error(error as undefined);
      toast.error("Error encountered saving changes to merchant's details");
    }
  });
